'use client';

import { useEffect, useRef, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import { resolveFallbackImageUrl, type ResolvedDefaultImages } from '@/components/common/SafeImage';
import type { HeroSlideViewModel } from '@/lib/home-view-model';
import { sanitizeHeroDesign, type HeroDesign } from '@/lib/theme-settings';
import { HeroCarouselShell } from '@/themes/legacy/hero/HeroCarouselShell';
import { heroSlideStyles } from '@/themes/legacy/hero/heroSlideStyles';

// A configured height too small for title + description + CTA + the arrow band would clip content. Below these the
// description is dropped (title and CTA always stay): mobile heights 240–700 and desktop heights 300–900 are all valid
// (src/lib/theme-settings.ts), so the theme must stay usable at both ends.
const COMPACT_MOBILE_BELOW = 380;
const COMPACT_DESKTOP_BELOW = 440;

type Theme03HeroProps = {
  slides: HeroSlideViewModel[];
  design: HeroDesign;
  defaults: ResolvedDefaultImages;
};

/**
 * Marble.Theme.03 hero: a full-bleed image inside the MAIN CONTAINER (never wider than the content), a left veil, and
 * editorial text anchored bottom-left with the arrows bottom-right. It sits ABOVE the filter/content grid.
 */
export function Theme03Hero({ slides, design: designProp, defaults }: Theme03HeroProps) {
  const design = sanitizeHeroDesign(designProp);

  return (
    <HeroCarouselShell variantClassName="hero-carousel--premium" design={design}>
      {slides.map((slide, index) => (
        <SwiperSlide key={slide.id}>
          <Theme03HeroSlide slide={slide} design={design} defaults={defaults} isFirst={index === 0} />
        </SwiperSlide>
      ))}
    </HeroCarouselShell>
  );
}

function Theme03HeroSlide({ slide, design, defaults, isFirst }: { slide: HeroSlideViewModel; design: HeroDesign; defaults: ResolvedDefaultImages; isFirst: boolean }) {
  // One page-level <h1> (the first slide); the others are <h2> so the document outline stays valid.
  const Title = isFirst ? 'h1' : 'h2';
  const { heightClass, titleStyle, descriptionStyle, subtitleStyle, ctaClass, ctaStyle } = heroSlideStyles(design);
  const compactMobile = design.mobileHeight !== undefined && design.mobileHeight < COMPACT_MOBILE_BELOW;
  const compactDesktop = design.desktopHeight !== undefined && design.desktopHeight < COMPACT_DESKTOP_BELOW;

  return (
    <div
      className={`t3-hero-slide${compactMobile ? ' t3-hero-slide--compact-mobile' : ''}${compactDesktop ? ' t3-hero-slide--compact-desktop' : ''}${heightClass}`}
    >
      <Theme03HeroImage image={slide.image} mobileImage={slide.mobileImage} defaults={defaults} />
      <div className="t3-hero-slide__veil" />

      <div className="t3-hero-slide__content">
        {slide.subtitle ? (
          <p className="t3-hero-slide__eyebrow" style={subtitleStyle}>
            {slide.subtitle}
          </p>
        ) : null}

        <Title className="t3-hero-slide__title" style={titleStyle('0ms')}>
          {slide.title}
        </Title>

        {slide.description ? (
          <p className="t3-hero-slide__text" style={descriptionStyle('0ms')}>
            {slide.description}
          </p>
        ) : null}

        {slide.cta ? (
          <a href={slide.cta.link} className={`t3-hero-slide__cta${ctaClass}`} style={ctaStyle('0ms')}>
            {slide.cta.text}
            <span aria-hidden="true">→</span>
          </a>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The slide picture. Decorative (the slide's text carries the meaning), so `alt=""`. A <picture> serves the dedicated
 * mobile image below md and the desktop image otherwise — a single request, unlike two stacked layers. Same fallback
 * chain as <SafeImage> (real image -> admin default banner -> bundled placeholder), advanced by onError, so a missing or
 * broken URL never leaves a broken-image box: 0 = as configured, 1 = the desktop image only (the mobile one failed),
 * 2 = the default. The chain is finite (no loop).
 */
function Theme03HeroImage({ image, mobileImage, defaults }: { image: string; mobileImage?: string; defaults: ResolvedDefaultImages }) {
  const [state, setState] = useState({ key: `${image}|${mobileImage ?? ''}`, step: 0 });
  const key = `${image}|${mobileImage ?? ''}`;
  const step = state.key === key ? state.step : 0;
  const fallback = resolveFallbackImageUrl(null, 'banner', defaults);
  const hasMobile = Boolean(mobileImage) && mobileImage !== image;
  const src = step >= 2 || !image?.trim() ? fallback : image;
  const advance = () => setState({ key, step: Math.min(step + 1, 2) });
  const imgRef = useRef<HTMLImageElement>(null);

  // The server HTML is painted before React attaches onError: an image that already failed by then never fires the event
  // again, so look at the element once on mount / whenever the chain moves.
  useEffect(() => {
    const el = imgRef.current;
    // (SVGs without an intrinsic size legitimately report 0 in some browsers, so they are left to onError.)
    if (el && el.complete && el.naturalWidth === 0 && el.getAttribute('src') !== fallback && !/\.svg(\?|$)/i.test(el.currentSrc)) {
      setState({ key, step: Math.min(step + 1, 2) });
    }
  }, [key, step, fallback]);

  return (
    <picture>
      {hasMobile && step === 0 ? <source media="(max-width: 767px)" srcSet={mobileImage} /> : null}
      <img ref={imgRef} className="t3-hero-slide__image" src={src} alt="" onError={src === fallback ? undefined : advance} />
    </picture>
  );
}
